import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Search, RefreshCw, Camera, ChevronLeft, ChevronRight, ImageOff } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

/**
 * AlyAllNationalOrdersPage — "All National Orders" search for Aly.
 *
 * Purpose: let Aly look up ANY National order (past, archived, completed,
 * transmitted, or current) so she can find an old order and review the
 * photos that were already uploaded for it.
 *
 * Scope (enforced server-side for National Transmission role):
 *   • company = National ONLY (Safeguard is never returned to her)
 *   • includes archived / completed / transmitted / paid — lookup is the
 *     whole point
 *   • read-only; opening an order uses /aly-order/:id (her view-only detail)
 */
const AlyAllNationalOrdersPage = ({ Layout }) => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [status, setStatus] = useState("");
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // Debounce the search box so we don't hammer the API on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [search]);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const params = { company: "National", page, limit };
      if (debouncedSearch) params.search = debouncedSearch;
      if (status) params.status = status;
      const r = await axios.get(`${API}/orders/all-company`, { params });
      setRows(r.data?.orders || []);
      setTotal(r.data?.total || 0);
      setStatuses(r.data?.filters?.statuses || []);
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed to load National orders");
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, status, page, limit]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const pages = Math.max(1, Math.ceil(total / limit));
  const openOrder = (id) => navigate(`/aly-order/${id}`);

  const statusColor = (s) => {
    const v = (s || "").toLowerCase();
    if (v.includes("archive") || v.includes("completed")) return "bg-gray-200 text-gray-600";
    if (v.includes("transmit") || v.includes("paid")) return "bg-green-100 text-green-700";
    if (v.includes("qa")) return "bg-blue-100 text-blue-700";
    if (v.includes("assign") || v.includes("field") || v.includes("inspection")) return "bg-amber-100 text-amber-800";
    if (v.includes("hold") || v.includes("need") || v.includes("pending")) return "bg-rose-100 text-rose-700";
    return "bg-slate-100 text-slate-600";
  };

  const body = (
    <div className="p-4 md:p-6 space-y-4" data-testid="aly-all-national-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-[#1F2937] flex items-center gap-2">
            <Camera className="h-5 w-5 text-[#9A7735]" />
            All National Orders
          </h1>
          <p className="text-xs text-gray-500 mt-0.5">
            Lookup any National order — past, archived, transmitted, or active — to review uploaded photos.
          </p>
        </div>
        <button
          onClick={fetchOrders}
          className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-medium rounded-lg flex items-center gap-1.5"
          data-testid="aly-all-national-refresh"
        >
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-3 flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[240px]">
          <label className="block text-[10px] uppercase text-gray-500 mb-1">Search</label>
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Order #, address, city, client..."
              className="w-full pl-7 pr-2 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-[#9A7735]/40"
              data-testid="aly-all-national-search"
            />
          </div>
        </div>
        <div>
          <label className="block text-[10px] uppercase text-gray-500 mb-1">Status</label>
          <select
            value={status}
            onChange={(e) => { setStatus(e.target.value); setPage(1); }}
            className="py-1.5 px-2 text-sm border border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-[#9A7735]/40"
            data-testid="aly-all-national-status"
          >
            <option value="">All statuses</option>
            {statuses.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
        <div className="text-[11px] text-gray-500 whitespace-nowrap">
          {loading ? "Loading…" : `${total.toLocaleString()} orders found`}
        </div>
      </div>

      {err && <div className="p-3 bg-rose-50 text-rose-700 text-sm rounded">{err}</div>}

      {/* Results */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-[10px] uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-3 py-2 text-left">Order #</th>
                <th className="px-3 py-2 text-left">Address</th>
                <th className="px-3 py-2 text-left">City</th>
                <th className="px-3 py-2 text-left">Flow</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-left">Inspector</th>
                <th className="px-3 py-2 text-left">Updated</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-3 py-8 text-center text-gray-400 text-sm">
                    <ImageOff className="mx-auto h-6 w-6 mb-2 text-gray-300" />
                    No National orders match your filter.
                  </td>
                </tr>
              )}
              {rows.map((o) => (
                <tr
                  key={o.id}
                  className="border-t border-gray-100 hover:bg-[#FDFBF4] cursor-pointer"
                  onClick={() => openOrder(o.id)}
                  data-testid={`aly-all-national-row-${o.order_number}`}
                >
                  <td className="px-3 py-2 font-semibold text-[#1F2937]">{o.order_number || "—"}</td>
                  <td className="px-3 py-2 text-gray-700">{o.address || "—"}</td>
                  <td className="px-3 py-2 text-gray-600">{o.city || "—"}</td>
                  <td className="px-3 py-2 text-gray-600">{o.flow_type || "—"}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-semibold ${statusColor(o.status)}`}>
                      {o.status || "—"}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-gray-600 text-xs">{o.inspector || "—"}</td>
                  <td className="px-3 py-2 text-gray-500 text-[11px] whitespace-nowrap">
                    {o.updated_at ? String(o.updated_at).slice(0, 16).replace("T", " ") : "—"}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      onClick={(e) => { e.stopPropagation(); openOrder(o.id); }}
                      className="px-2 py-1 bg-[#F4E3C8] hover:bg-[#EAD4A8] text-[#9A7735] text-[11px] font-semibold rounded"
                      data-testid={`aly-open-order-${o.order_number}`}
                    >
                      Open
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between p-3 border-t border-gray-100 text-xs text-gray-500">
          <span>Page {page} of {pages}</span>
          <div className="flex items-center gap-1">
            <button
              disabled={page <= 1 || loading}
              onClick={() => setPage(p => Math.max(1, p - 1))}
              className="p-1 rounded hover:bg-gray-100 disabled:opacity-40"
              data-testid="aly-all-national-prev"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              disabled={page >= pages || loading}
              onClick={() => setPage(p => Math.min(pages, p + 1))}
              className="p-1 rounded hover:bg-gray-100 disabled:opacity-40"
              data-testid="aly-all-national-next"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return Layout ? <Layout>{body}</Layout> : body;
};

export default AlyAllNationalOrdersPage;
